import { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import {
    ChevronLeft,
    ChevronRight,
    ClipboardList
} from 'lucide-react';
import { Ticket as TicketType } from '../../types';

interface CalendarDay {
    date: Date;
    isCurrentMonth: boolean;
    tickets: TicketType[];
}

export default function TicketCalendar() {
    const { tickets, setActiveView } = useApp();
    const [currentDate, setCurrentDate] = useState(new Date());

    // Get first and last day of the month
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const calendarDays = useMemo(() => {
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDay = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();

        const days: CalendarDay[] = [];

        // Previous month padding
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = startDay - 1; i >= 0; i--) {
            const date = new Date(year, month - 1, prevMonthLastDay - i);
            days.push({
                date,
                isCurrentMonth: false,
                tickets: getTicketsForDate(date)
            });
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push({
                date,
                isCurrentMonth: true,
                tickets: getTicketsForDate(date)
            });
        }

        // Next month padding
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(year, month + 1, day);
            days.push({
                date,
                isCurrentMonth: false,
                tickets: getTicketsForDate(date)
            });
        }

        return days;
    }, [year, month, tickets]);

    function getTicketsForDate(date: Date): TicketType[] {
        return tickets.filter(ticket => {
            if (!ticket.due_date) return false;
            const dueDate = new Date(ticket.due_date);
            return dueDate.getFullYear() === date.getFullYear() &&
                dueDate.getMonth() === date.getMonth() &&
                dueDate.getDate() === date.getDate();
        });
    }

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'var(--accent-danger)';
            case 'high': return 'var(--color-warning)';
            case 'medium': return 'var(--accent-info)';
            case 'low': return 'var(--text-muted)';
            default: return 'var(--text-muted)';
        }
    };

    const goToPrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const goToNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
    const goToToday = () => setCurrentDate(new Date());

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate();
    };

    return (
        <div style={{
            padding: 'var(--spacing-md)',
            height: '100%',
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-md)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-sm)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <ClipboardList size={24} color="var(--accent-primary)" />
                    <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                        {monthNames[month]} {year}
                    </h2>
                </div>

                <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                    <button
                        onClick={goToPrevMonth}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--spacing-xs)' }}
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={goToToday}
                        className="btn btn-secondary"
                        style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}
                    >
                        Today
                    </button>
                    <button
                        onClick={goToNextMonth}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--spacing-xs)' }}
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {/* Day Headers */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                marginBottom: 'var(--spacing-xs)'
            }}>
                {dayNames.map(day => (
                    <div key={day} style={{
                        textAlign: 'center',
                        padding: 'var(--spacing-xs)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase'
                    }}>
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                flex: 1,
                overflow: 'auto',
                background: 'var(--border-color)'
            }}>
                {calendarDays.map((day, idx) => (
                    <div
                        key={idx}
                        style={{
                            background: day.isCurrentMonth ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                            padding: 'var(--spacing-xs)',
                            minHeight: 80,
                            cursor: day.tickets.length > 0 ? 'pointer' : 'default',
                            position: 'relative'
                        }}
                    >
                        {/* Day Number */}
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: isToday(day.date) ? 700 : 400,
                            color: day.isCurrentMonth
                                ? isToday(day.date) ? 'var(--accent-primary)' : 'var(--text-primary)'
                                : 'var(--text-muted)',
                            marginBottom: 'var(--spacing-xs)',
                            width: isToday(day.date) ? 24 : 'auto',
                            height: isToday(day.date) ? 24 : 'auto',
                            borderRadius: '50%',
                            background: isToday(day.date) ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {day.date.getDate()}
                        </div>

                        {/* Tickets */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {day.tickets.slice(0, 3).map(ticket => (
                                <div
                                    key={ticket.id}
                                    onClick={() => setActiveView({ type: 'ticket', id: ticket.id })}
                                    style={{
                                        fontSize: 10,
                                        padding: '2px 4px',
                                        borderRadius: 'var(--radius-sm)',
                                        background: getPriorityColor(ticket.priority),
                                        color: 'white',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer'
                                    }}
                                    title={`#${ticket.ticket_number} ${ticket.title}`}
                                >
                                    #{ticket.ticket_number}
                                </div>
                            ))}
                            {day.tickets.length > 3 && (
                                <div style={{
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                    textAlign: 'center'
                                }}>
                                    +{day.tickets.length - 3} more
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                marginTop: 'var(--spacing-md)',
                justifyContent: 'center',
                flexWrap: 'wrap',
                fontSize: 'var(--font-size-xs)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--accent-danger)' }} />
                    <span>Urgent</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--accent-warning)' }} />
                    <span>High</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--accent-info)' }} />
                    <span>Medium</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--text-muted)' }} />
                    <span>Low</span>
                </div>
            </div>
        </div>
    );
}
